import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import {
  ArrowLeft, Mail, Phone, MapPin, Star, Briefcase, Calendar,
  ShieldCheck, ShieldX, FileText, X
} from 'lucide-react';
import { format } from 'date-fns';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const UPLOADS_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const data = await adminAPI.getUserById(id);
        setUser(data);
      } catch (err) {
        console.error('Failed to load user:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [id]);

  const handleStatusChange = async (newStatus) => {
    setActionLoading(true);
    try {
      await adminAPI.updateUserStatus(id, newStatus, suspendReason || undefined);
      setUser(prev => ({ ...prev, status: newStatus }));
      setShowSuspendModal(false);
      setSuspendReason('');
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-20">
        <p className="text-navy-500">User not found.</p>
        <button onClick={() => navigate('/admin/users')} className="btn-primary mt-4">Back to Users</button>
      </div>
    );
  }

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'activity', label: 'Activity' },
    { key: 'ratings', label: 'Ratings' },
    ...(user.user_type === 'helper' ? [{ key: 'helper', label: 'Helper Profile' }] : []),
  ];

  return (
    <div className="space-y-6">
      {/* Back + Header */}
      <button onClick={() => navigate('/admin/users')} className="flex items-center gap-2 text-sm text-navy-500 hover:text-navy-700">
        <ArrowLeft className="h-4 w-4" /> Back to Users
      </button>

      <div className="card">
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <div className="h-16 w-16 bg-navy-200 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
            {user.profile_photo_url ? (
              <img src={user.profile_photo_url.startsWith('http') ? user.profile_photo_url : `${UPLOADS_BASE_URL}${user.profile_photo_url}`} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="text-navy-600 text-xl font-medium">{user.first_name?.[0]}{user.last_name?.[0]}</span>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl font-display font-semibold text-navy-900">{user.first_name} {user.last_name}</h1>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                user.user_type === 'helper' ? 'bg-purple-100 text-purple-700' :
                user.user_type === 'admin' ? 'bg-red-100 text-red-700' :
                'bg-primary-100 text-primary-700'
              }`}>
                {user.user_type?.replace('_', ' ')}
              </span>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                user.status === 'active' ? 'bg-green-100 text-green-700' :
                user.status === 'suspended' ? 'bg-red-100 text-red-700' :
                'bg-yellow-100 text-yellow-700'
              }`}>
                {user.status}
              </span>
            </div>
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-navy-500">
              <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {user.email}</span>
              {user.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {user.phone}</span>}
              <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Joined {user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : '-'}</span>
            </div>
          </div>
          <div className="flex gap-2">
            {user.status === 'active' ? (
              <button onClick={() => setShowSuspendModal(true)} className="px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-apple hover:bg-red-50 transition-colors">
                Suspend
              </button>
            ) : user.status === 'suspended' ? (
              <button onClick={() => handleStatusChange('active')} disabled={actionLoading} className="px-3 py-1.5 text-sm font-medium text-green-600 border border-green-200 rounded-apple hover:bg-green-50 transition-colors">
                {actionLoading ? 'Activating...' : 'Reactivate'}
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-navy-100 p-1 rounded-apple w-fit">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium rounded-apple transition-all duration-200 ${
              tab === t.key ? 'bg-white text-navy-900 shadow-apple-sm' : 'text-navy-500 hover:text-navy-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card">
            <h3 className="text-sm font-semibold text-navy-700 mb-3">Account Info</h3>
            <div className="space-y-2 text-sm">
              <Row label="User ID" value={user.id} />
              <Row label="Email" value={user.email} />
              <Row label="Phone" value={user.phone || '-'} />
              <Row label="Language" value={user.language || 'en'} />
              <Row label="Created" value={user.created_at ? format(new Date(user.created_at), 'PPp') : '-'} />
              <Row label="Updated" value={user.updated_at ? format(new Date(user.updated_at), 'PPp') : '-'} />
            </div>
          </div>
          {user.user_type === 'yacht_owner' && user.yachts && user.yachts.length > 0 && (
            <div className="card">
              <h3 className="text-sm font-semibold text-navy-700 mb-3">Yachts</h3>
              <div className="space-y-3">
                {user.yachts.map(y => (
                  <div key={y.id} className="bg-navy-50 rounded-apple p-3">
                    <p className="font-medium text-navy-900">{y.name}</p>
                    <p className="text-xs text-navy-500 mt-1">{y.yacht_type} - {y.length_m}m</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'activity' && (
        <div className="space-y-6">
          {user.recentRequests && user.recentRequests.length > 0 && (
            <div className="card">
              <h3 className="text-sm font-semibold text-navy-700 mb-3">Recent Requests</h3>
              <div className="space-y-2">
                {user.recentRequests.map(r => (
                  <div key={r.id} className="flex items-center justify-between py-2 border-b border-navy-100 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-navy-900">{r.port_name || 'Unknown'}</p>
                      <p className="text-xs text-navy-500">{r.scheduled_date || ''}</p>
                    </div>
                    <StatusBadge status={r.status} />
                  </div>
                ))}
              </div>
            </div>
          )}
          {user.recentServices && user.recentServices.length > 0 && (
            <div className="card">
              <h3 className="text-sm font-semibold text-navy-700 mb-3">Recent Services</h3>
              <div className="space-y-2">
                {user.recentServices.map(s => (
                  <div key={s.id} className="flex items-center justify-between py-2 border-b border-navy-100 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-navy-900">{s.port_name || 'Service'}</p>
                      <p className="text-xs text-navy-500">{s.scheduled_date || ''}</p>
                    </div>
                    <StatusBadge status={s.status} />
                  </div>
                ))}
              </div>
            </div>
          )}
          {(!user.recentRequests?.length && !user.recentServices?.length) && (
            <div className="card p-8 text-center text-navy-400">No activity yet</div>
          )}
        </div>
      )}

      {tab === 'ratings' && (
        <div className="card">
          <h3 className="text-sm font-semibold text-navy-700 mb-3">Ratings Received</h3>
          {user.ratingsReceived && user.ratingsReceived.length > 0 ? (
            <div className="space-y-3">
              {user.ratingsReceived.map(r => (
                <div key={r.id} className="bg-navy-50 rounded-apple p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-navy-900">{r.rater_first_name} {r.rater_last_name}</p>
                    <div className="flex items-center gap-1">
                      <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                      <span className="text-sm font-semibold">{r.rating}</span>
                    </div>
                  </div>
                  {r.comment && <p className="text-sm text-navy-600 mt-1">{r.comment}</p>}
                  <p className="text-xs text-navy-400 mt-1">{r.created_at ? format(new Date(r.created_at), 'MMM d, yyyy') : ''}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-navy-400 text-center py-4">No ratings yet</p>
          )}
        </div>
      )}

      {tab === 'helper' && user.helperProfile && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card">
            <h3 className="text-sm font-semibold text-navy-700 mb-3">Helper Profile</h3>
            <div className="space-y-2 text-sm">
              <Row label="Verification" value={user.helperProfile.verification_status} />
              <Row label="Experience" value={`${user.helperProfile.experience_years || 0} years`} />
              <Row label="Total Jobs" value={user.helperProfile.total_jobs || 0} />
              <Row label="Rating" value={user.helperProfile.average_rating ? `${user.helperProfile.average_rating.toFixed(1)} (${user.helperProfile.rating_count} reviews)` : 'No ratings'} />
              <Row label="Available" value={user.helperProfile.available_now ? 'Yes' : 'No'} />
              {user.helperProfile.bio && <div><p className="text-navy-500 mb-1">Bio</p><p className="text-navy-700">{user.helperProfile.bio}</p></div>}
            </div>
            {user.helperProfile.skills?.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-navy-500 mb-2">Skills</p>
                <div className="flex flex-wrap gap-1.5">
                  {user.helperProfile.skills.map(s => (
                    <span key={s} className="px-2 py-0.5 bg-navy-100 text-navy-700 text-xs rounded-full">{s}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="card">
            <h3 className="text-sm font-semibold text-navy-700 mb-3">Documents ({user.documents?.length || 0})</h3>
            {user.documents && user.documents.length > 0 ? (
              <div className="space-y-2">
                {user.documents.map(d => (
                  <div key={d.id} className="flex items-center gap-3 bg-navy-50 rounded-apple p-3">
                    <FileText className="h-5 w-5 text-navy-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-navy-900 truncate">{d.file_name}</p>
                      <p className="text-xs text-navy-500">{d.document_type} - {d.uploaded_at ? format(new Date(d.uploaded_at), 'MMM d, yyyy') : ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-navy-400 text-center py-4">No documents uploaded</p>
            )}
            {user.serviceAreas && user.serviceAreas.length > 0 && (
              <div className="mt-4">
                <h4 className="text-xs text-navy-500 mb-2">Service Areas</h4>
                <div className="flex flex-wrap gap-1.5">
                  {user.serviceAreas.map(sa => (
                    <span key={sa.id} className="px-2 py-0.5 bg-primary-50 text-primary-700 text-xs rounded-full flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {sa.port_name || sa.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Suspend Modal */}
      {showSuspendModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-apple-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-display font-semibold text-navy-900">Suspend User</h3>
              <button onClick={() => setShowSuspendModal(false)} className="p-1 hover:bg-navy-100 rounded-apple">
                <X className="h-5 w-5 text-navy-500" />
              </button>
            </div>
            <p className="text-sm text-navy-600 mb-4">
              Suspend <strong>{user.first_name} {user.last_name}</strong>? They won't be able to login or use the platform.
            </p>
            <textarea
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Reason for suspension (optional)..."
              className="input-field w-full mb-4"
              rows={3}
            />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowSuspendModal(false)} className="btn-secondary">Cancel</button>
              <button
                onClick={() => handleStatusChange('suspended')}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-pill text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Suspending...' : 'Suspend User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-navy-100 last:border-0">
      <span className="text-navy-500">{label}</span>
      <span className="text-navy-900 font-medium">{value}</span>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    pending: 'bg-yellow-100 text-yellow-800', active: 'bg-blue-100 text-blue-800',
    matched: 'bg-indigo-100 text-indigo-800', confirmed: 'bg-primary-100 text-primary-800',
    in_progress: 'bg-green-100 text-green-800', completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800', no_show: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-navy-100 text-navy-700'}`}>
      {status?.replace('_', ' ')}
    </span>
  );
}
